import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map } from 'rxjs';

import { User } from '@surface-elements/users/domain';
import { environment } from '@surface-elements/shared/environments';
import type {
  ApiKey,
  CreateApiKeyResponse,
  RevokeApiKeyResponse,
  RevokeUserTokensResponse,
  UserTokensResponse,
} from './+state/admin-users.state';

/**
 * Raw backend response shape from GET /api/v1/admin/users.
 * TODO(SEOMS-337): Remove when User type matches backend schema.
 */
interface BackendUserResponse {
  _id: string;
  firstName: string;
  lastName: string;
  email: { emailAddress: string; type: string };
  roles: string[];
  createdAt: string;
  updatedAt: string;
}

export interface UpdateRolesResponse {
  userId: string;
  email: string;
  roles: string[];
  previousRoles: string[];
}

@Injectable({
  providedIn: 'root',
})
export class AdminUsersService {
  private http = inject(HttpClient);
  private url = `${environment.apiUrl}/admin`;

  getUsers(): Observable<User[]> {
    return this.http.get<BackendUserResponse[]>(`${this.url}/users`).pipe(
      // TODO(SEOMS-337): Remove flattening when User type is refactored
      map(users =>
        users.map(u => ({
          ...u,
          emailAddress: u.email?.emailAddress,
        })),
      ),
    );
  }

  updateUserRoles(
    userId: string,
    roles: string[],
  ): Observable<UpdateRolesResponse> {
    return this.http.patch<UpdateRolesResponse>(
      `${this.url}/users/${userId}/roles`,
      { roles },
    );
  }

  addRole(
    userId: string,
    currentRoles: string[],
    role: string,
  ): Observable<UpdateRolesResponse> {
    const roles = currentRoles.includes(role)
      ? [...currentRoles]
      : [...currentRoles, role];
    return this.updateUserRoles(userId, roles);
  }

  removeRole(
    userId: string,
    currentRoles: string[],
    role: string,
  ): Observable<UpdateRolesResponse> {
    const roles = currentRoles.filter(r => r !== role);
    if (roles.length === 0) {
      return throwError(() => new Error('Cannot remove last role'));
    }
    return this.updateUserRoles(userId, roles);
  }

  // API Key management (SEOMS-349)
  listApiKeys(): Observable<ApiKey[]> {
    return this.http.get<ApiKey[]>(`${this.url}/api-keys`);
  }

  createApiKey(params: {
    name: string;
    userId: string;
  }): Observable<CreateApiKeyResponse> {
    return this.http.post<CreateApiKeyResponse>(`${this.url}/api-keys`, params);
  }

  revokeApiKey(keyId: string): Observable<RevokeApiKeyResponse> {
    return this.http.delete<RevokeApiKeyResponse>(
      `${this.url}/api-keys/${keyId}`,
    );
  }

  revokeUserTokens(userId: string): Observable<RevokeUserTokensResponse> {
    return this.http.delete<RevokeUserTokensResponse>(
      `${this.url}/users/${userId}/tokens`,
    );
  }

  getUserTokens(userId: string): Observable<UserTokensResponse> {
    return this.http.get<UserTokensResponse>(
      `${this.url}/users/${userId}/tokens`,
    );
  }
}
